import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  selectAwayTeam,
  selectCornerStats,
  selectFreeKickStats,
  selectHomeTeam,
  selectMatchStatusId,
  selectOffsideStats,
  selectShotsOnGoal,
  selectPostHits,
  selectShotsOffTarget,
  selectChances,
  selectTotalShotsStats,
  selectRedCardStats,
  selectYellowCardStats,
  selectSaves,
  selectYellowCards,
  selectRedCards,
  selectTotalShots,
  selectOffsides,
  selectCorners,
  selectFreeKicks,
} from 'features/match/matchSlice';

import {
  statsSliceActions,
  selectShotsOnGoalField,
  selectPostHitsField,
  selectShotsOffTargetField,
  selectChancesField,
  selectSavesField,
} from 'features/stats/statsSlice';

import AutomaticField from './AutomaticField';
import DynamicField from './DynamicField';

import StyledStats from './StyledStats';
import { MatchStatus } from '../../enums/matchStatus';
import { MatchEventType } from '../../enums/matchEventType';

const Stats: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const homeTeam = useSelector(selectHomeTeam);
  const awayTeam = useSelector(selectAwayTeam);

  const totalShotsStats = useSelector(selectTotalShotsStats);
  const redCardStats = useSelector(selectRedCardStats);
  const yellowCardStats = useSelector(selectYellowCardStats);
  const cornerStats = useSelector(selectCornerStats);
  const offsideStats = useSelector(selectOffsideStats);
  const freeKickStats = useSelector(selectFreeKickStats);

  const shotsOnGoalField = useSelector(selectShotsOnGoalField);
  const shotsOnGoal = useSelector(selectShotsOnGoal);

  const postHitsField = useSelector(selectPostHitsField);
  const postHits = useSelector(selectPostHits);

  const shotsOffTargetField = useSelector(selectShotsOffTargetField);
  const shotsOffTarget = useSelector(selectShotsOffTarget);

  const chancesField = useSelector(selectChancesField);
  const chances = useSelector(selectChances);

  const savesField = useSelector(selectSavesField);
  const saves = useSelector(selectSaves);

  const yellowCards = useSelector(selectYellowCards);
  const redCards = useSelector(selectRedCards);
  const totalShots = useSelector(selectTotalShots);
  const offsides = useSelector(selectOffsides);
  const corners = useSelector(selectCorners);
  const freeKicks = useSelector(selectFreeKicks);

  const matchStatusId = useSelector(selectMatchStatusId);
  const disableStats = matchStatusId === MatchStatus.NOT_STARTED;

  return (
    <StyledStats>
      <div className="tabs">
        <div className="tab active">
          <FormattedMessage id="stats.title" />
        </div>
      </div>
      <div className="content">
        <div className="ball-possession">
          <div className="team-name">{homeTeam.name}</div>
          <div className="team-name">{awayTeam.name}</div>
        </div>
        <div className="dynamic">
          <DynamicField
            label={intl.formatMessage({ id: 'stats.shotsOnGoal' })}
            onAutoToggle={() => {
              dispatch(statsSliceActions.toggleAutoOnField('shotsOnGoal'));
            }}
            statsSelect={shotsOnGoal}
            statField={shotsOnGoalField}
            disabled={disableStats}
          />
          <DynamicField
            label={intl.formatMessage({ id: 'stats.postHits' })}
            onAutoToggle={() => {
              dispatch(statsSliceActions.toggleAutoOnField('hittingThePost'));
            }}
            statsSelect={postHits}
            statField={postHitsField}
            disabled={disableStats}
          />
          <DynamicField
            label={intl.formatMessage({ id: 'stats.shotsOffTarget' })}
            onAutoToggle={() => {
              dispatch(statsSliceActions.toggleAutoOnField('shotsOffTarget'));
            }}
            statsSelect={shotsOffTarget}
            statField={shotsOffTargetField}
            disabled={disableStats}
          />
          <DynamicField
            label={intl.formatMessage({ id: 'stats.chances' })}
            onAutoToggle={() => {
              dispatch(statsSliceActions.toggleAutoOnField('chances'));
            }}
            statsSelect={chances}
            statField={chancesField}
            disabled={disableStats}
          />
          <DynamicField
            label={intl.formatMessage({ id: 'stats.saves' })}
            onAutoToggle={() => {
              dispatch(statsSliceActions.toggleAutoOnField('saves'));
            }}
            statsSelect={saves}
            statField={savesField}
            disabled={disableStats}
          />
        </div>
        <div className="automatic">
          <AutomaticField
            label={intl.formatMessage({ id: 'stats.yellowCards' })}
            data={yellowCardStats}
            statsSelect={yellowCards}
          />
          <AutomaticField
            label={intl.formatMessage({ id: 'stats.redCards' })}
            data={redCardStats}
            statsSelect={redCards}
          />
          <AutomaticField
            label={intl.formatMessage({ id: 'stats.totalShots' })}
            data={totalShotsStats}
            statsSelect={totalShots}
          />
          <AutomaticField
            label={intl.formatMessage({ id: `events.${MatchEventType.CORNER}` })}
            data={cornerStats}
            statsSelect={corners}
          />
          <AutomaticField
            label={intl.formatMessage({ id: `events.${MatchEventType.OFFSIDE}` })}
            data={offsideStats}
            statsSelect={offsides}
          />
          <AutomaticField
            label={intl.formatMessage({ id: `events.${MatchEventType.FREE_KICK}` })}
            data={freeKickStats}
            statsSelect={freeKicks}
          />
        </div>
      </div>
    </StyledStats>
  );
};

export default Stats;
